import React, { useState, useEffect } from 'react';
import { CreditCard, Wallet, Save } from 'lucide-react';

const PaymentModal = ({ isOpen, onClose, invoice, onSubmit }) => {
    const [paymentData, setPaymentData] = useState({
        method: 'cash',
        transactionId: '',
        upiId: '',
        chequeNumber: '',
        bankName: '',
        chequeDate: '',
        accountNumber: '',
        ifscCode: '',
        transactionReference: ''
    });

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(paymentData);
    };

    if (!isOpen) return null;

    // Glassmorphism Styles
    const styles = {
        // Overlay - Fixed fullscreen with scroll capability
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'flex-start',  // Changed from center to flex-start
            justifyContent: 'center',
            zIndex: 1050,
            padding: '20px',
            overflowY: 'auto',  // Allow overlay to scroll
            overflowX: 'hidden'
        },
        // Modal Container - Scrollable
        modalContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            padding: '20px 0'
        },
        // Modal Content
        modalContent: {
            maxWidth: '650px',
            width: '100%',
            maxHeight: '85vh',  // Limit height
            background: 'var(--bg-card)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            borderRadius: '20px',
            border: '1px solid var(--border)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',  // Stack header, body, footer
            overflow: 'hidden',  // Clip content to border-radius
            margin: 'auto'
        },
        modalGlow: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'var(--accent)',
            zIndex: 10
        },
        // Header - Fixed at top
        header: {
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            padding: '20px 25px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexShrink: 0  // Prevent shrinking
        },
        headerTitle: {
            margin: 0,
            fontSize: '22px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            textShadow: 'none'
        },
        closeButton: {
            color: 'var(--text-secondary)',
            fontSize: '28px',
            background: 'var(--bg-page)',
            border: '1px solid var(--border)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            lineHeight: 1,
            flexShrink: 0
        },
        // Body - Scrollable
        body: {
            padding: '25px',
            overflowY: 'auto',  // Enable scrolling
            overflowX: 'hidden',
            flex: 1,  // Take remaining space
            // Custom scrollbar styles
            scrollbarWidth: 'thin',
            scrollbarColor: '#38bdf8 rgba(15, 23, 42, 0.5)'
        },
        // Footer - Fixed at bottom
        footer: {
            padding: '20px 25px',
            background: 'var(--bg-card)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            flexShrink: 0,  // Prevent shrinking
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
        },
        invoiceSummary: {
            marginBottom: '25px',
            padding: '18px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            borderLeft: '4px solid var(--accent)',
            boxShadow: 'none',
            border: '1px solid var(--border)'
        },
        summaryRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
        },
        summaryLabel: {
            margin: '0 0 5px 0',
            fontSize: '13px',
            color: 'var(--text-secondary)',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        invoiceNumber: {
            margin: 0,
            fontSize: '18px',
            color: 'var(--text-primary)',
            fontWeight: '700',
            textShadow: 'none'
        },
        totalAmount: {
            margin: 0,
            fontSize: '24px',
            color: 'var(--accent)',
            fontWeight: '700',
            textShadow: 'none'
        },
        formGroup: {
            marginBottom: '25px'
        },
        formLabel: {
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '10px',
            color: 'var(--text-secondary)',
            display: 'block'
        },
        select: {
            padding: '12px 15px',
            fontSize: '15px',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            fontWeight: '500',
            width: '100%',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='%234b5563' d='M7 10L2 5h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 15px center'
        },
        input: {
            padding: '10px 12px',
            borderRadius: '8px',
            width: '100%',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
        },
        // UPI Section
        upiSection: {
            padding: '20px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid var(--border)',
            boxShadow: 'none'
        },
        upiTitle: {
            margin: '0 0 15px 0',
            fontSize: '16px',
            color: 'var(--text-primary)',
            textShadow: 'none'
        },
        // Cheque Section
        chequeSection: {
            padding: '20px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid var(--border)',
            boxShadow: 'none'
        },
        chequeTitle: {
            margin: '0 0 15px 0',
            fontSize: '16px',
            color: '#a78bfa',
            textShadow: 'none'
        },
        // Bank Transfer Section
        bankSection: {
            padding: '20px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid var(--border)',
            boxShadow: 'none'
        },
        bankTitle: {
            margin: '0 0 15px 0',
            fontSize: '16px',
            color: 'var(--text-primary)',
            textShadow: 'none'
        },
        // Online/Card Section
        onlineSection: {
            padding: '20px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid var(--border)',
            boxShadow: 'none'
        },
        onlineTitle: {
            margin: '0 0 15px 0',
            fontSize: '16px',
            color: 'var(--accent)',
            textShadow: '0 0 15px rgba(34, 197, 94, 0.3)'
        },
        // Cash Section
        cashSection: {
            padding: '18px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            border: '1px dashed var(--accent)',
            textAlign: 'center',
            marginBottom: '20px',
            boxShadow: 'none'
        },
        cashText: {
            margin: 0,
            fontSize: '15px',
            color: 'var(--accent)',
            fontWeight: '600'
        },
        cancelButton: {
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '10px',
            background: 'var(--bg-page)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
        },
        submitButton: {
padding: '12px 32px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '10px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
        },
        sectionLabel: {
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
            display: 'block'
        }
    };

    // Custom scrollbar CSS (inject into head)
    const scrollbarStyles = `
        .payment-modal-body::-webkit-scrollbar {
            width: 8px;
        }
        .payment-modal-body::-webkit-scrollbar-track {
            background: rgba(15, 23, 42, 0.5);
            border-radius: 4px;
        }
        .payment-modal-body::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #38bdf8, #0ea5e9);
            border-radius: 4px;
            border: 2px solid rgba(15, 23, 42, 0.5);
        }
        .payment-modal-body::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #0ea5e9, #0284c7);
        }
        .payment-modal-overlay::-webkit-scrollbar {
            width: 8px;
        }
        .payment-modal-overlay::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.3);
        }
        .payment-modal-overlay::-webkit-scrollbar-thumb {
            background: rgba(56, 189, 248, 0.5);
            border-radius: 4px;
        }
        .payment-modal-select option {
            background-color: var(--bg-card);
            color: var(--text-primary);
            padding: 10px;
        }
    `;

    // Get section-specific input border color
    const getInputBorderColor = (method) => {
        switch (method) {
            case 'upi': return 'rgba(251, 191, 36, 0.3)';
            case 'cheque': return 'rgba(139, 92, 246, 0.3)';
            case 'bank_transfer': return 'rgba(56, 189, 248, 0.3)';
            case 'online':
            case 'card': return 'rgba(34, 197, 94, 0.3)';
            default: return 'rgba(56, 189, 248, 0.2)';
        }
    };

    const getSectionInputStyle = (method) => ({
        ...styles.input,
        border: '1px solid var(--border)',
        background: 'var(--bg-card)'
    });

    const getFocusColor = (method) => {
        switch (method) {
            case 'upi': return '#fbbf24';
            case 'cheque': return '#a78bfa';
            case 'bank_transfer': return '#38bdf8';
            case 'online':
            case 'card': return '#22c55e';
            default: return '#38bdf8';
        }
    };

    const handleInputFocus = (e, method) => {
        const color = getFocusColor(method);
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${color}33, 0 0 15px ${color}26`;
    };

    const handleInputBlur = (e, method) => {
        e.currentTarget.style.borderColor = getInputBorderColor(method);
        e.currentTarget.style.boxShadow = 'none';
    };

    return (
        <>
            {/* Inject scrollbar styles */}
            <style>{scrollbarStyles}</style>

            <div
                className="payment-modal-overlay"
                style={styles.overlay}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div style={styles.modalContainer}>
                    <div
                        style={styles.modalContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Top Glow Effect */}
                        <div style={styles.modalGlow}></div>

                        {/* Header - Fixed */}
                        <div style={styles.header}>
                            <h2 style={{ ...styles.headerTitle, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CreditCard size={24} style={{ color: 'var(--accent)' }} /> Payment Details
                            </h2>
                            <button
                                style={styles.closeButton}
                                onClick={onClose}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(248, 113, 113, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(248, 113, 113, 0.4)';
                                    e.currentTarget.style.color = '#f87171';
                                    e.currentTarget.style.transform = 'rotate(90deg)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(100, 116, 139, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                                    e.currentTarget.style.color = '#94a3b8';
                                    e.currentTarget.style.transform = 'rotate(0deg)';
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            {/* Body - Scrollable */}
                            <div className="payment-modal-body" style={styles.body}>
                                {/* Invoice Summary */}
                                <div style={styles.invoiceSummary}>
                                    <div style={styles.summaryRow}>
                                        <div>
                                            <p style={styles.summaryLabel}>INVOICE NUMBER</p>
                                            <p style={styles.invoiceNumber}>{invoice?.invoiceNumber}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <p style={styles.summaryLabel}>TOTAL AMOUNT</p>
                                            <p style={styles.totalAmount}>₹{invoice?.grandTotal?.toFixed(2)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Method Selection */}
                                <div style={styles.formGroup}>
                                    <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', gap: '8px' }}><Wallet size={16} style={{ color: 'var(--accent)' }} /> Payment Method *</label>
                                    <select
                                        name="method"
                                        value={paymentData.method}
                                        onChange={handleChange}
                                        required
                                        className="payment-modal-select"
                                        style={styles.select}
                                        onFocus={(e) => {
                                            e.currentTarget.style.borderColor = '#38bdf8';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2), 0 0 20px rgba(56, 189, 248, 0.15)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <option value="cash">Cash</option>
                                        <option value="online">Online Payment</option>
                                        <option value="card">Card</option>
                                        <option value="upi">UPI</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="cheque">Cheque</option>
                                    </select>
                                </div>

                                {/* UPI Fields */}
                                {paymentData.method === 'upi' && (
                                    <div style={styles.upiSection}>
                                        <h4 style={styles.upiTitle}>UPI Payment Details</h4>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>UPI ID</label>
                                            <input
                                                type="text"
                                                name="upiId"
                                                value={paymentData.upiId}
                                                onChange={handleChange}
                                                placeholder="example@upi"
                                                style={getSectionInputStyle('upi')}
                                                onFocus={(e) => handleInputFocus(e, 'upi')}
                                                onBlur={(e) => handleInputBlur(e, 'upi')}
                                            />
                                        </div>
                                        <div>
                                            <label style={styles.sectionLabel}>Transaction ID</label>
                                            <input
                                                type="text"
                                                name="transactionId"
                                                value={paymentData.transactionId}
                                                onChange={handleChange}
                                                placeholder="Enter UPI transaction ID"
                                                style={getSectionInputStyle('upi')}
                                                onFocus={(e) => handleInputFocus(e, 'upi')}
                                                onBlur={(e) => handleInputBlur(e, 'upi')}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Cheque Fields */}
                                {paymentData.method === 'cheque' && (
                                    <div style={styles.chequeSection}>
                                        <h4 style={styles.chequeTitle}>Cheque Payment Details</h4>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>Cheque Number</label>
                                            <input
                                                type="text"
                                                name="chequeNumber"
                                                value={paymentData.chequeNumber}
                                                onChange={handleChange}
                                                placeholder="Enter cheque number"
                                                style={getSectionInputStyle('cheque')}
                                                onFocus={(e) => handleInputFocus(e, 'cheque')}
                                                onBlur={(e) => handleInputBlur(e, 'cheque')}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>Bank Name</label>
                                            <input
                                                type="text"
                                                name="bankName"
                                                value={paymentData.bankName}
                                                onChange={handleChange}
                                                placeholder="Enter bank name"
                                                style={getSectionInputStyle('cheque')}
                                                onFocus={(e) => handleInputFocus(e, 'cheque')}
                                                onBlur={(e) => handleInputBlur(e, 'cheque')}
                                            />
                                        </div>
                                        <div>
                                            <label style={styles.sectionLabel}>Cheque Date</label>
                                            <input
                                                type="date"
                                                name="chequeDate"
                                                value={paymentData.chequeDate}
                                                onChange={handleChange}
                                                style={{
                                                    ...getSectionInputStyle('cheque'),
                                                    colorScheme: 'dark'
                                                }}
                                                onFocus={(e) => handleInputFocus(e, 'cheque')}
                                                onBlur={(e) => handleInputBlur(e, 'cheque')}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Bank Transfer Fields */}
                                {paymentData.method === 'bank_transfer' && (
                                    <div style={styles.bankSection}>
                                        <h4 style={styles.bankTitle}>Bank Transfer Details</h4>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>Account Number</label>
                                            <input
                                                type="text"
                                                name="accountNumber"
                                                value={paymentData.accountNumber}
                                                onChange={handleChange}
                                                placeholder="Enter account number"
                                                style={getSectionInputStyle('bank_transfer')}
                                                onFocus={(e) => handleInputFocus(e, 'bank_transfer')}
                                                onBlur={(e) => handleInputBlur(e, 'bank_transfer')}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>IFSC Code</label>
                                            <input
                                                type="text"
                                                name="ifscCode"
                                                value={paymentData.ifscCode}
                                                onChange={handleChange}
                                                placeholder="Enter IFSC code"
                                                style={getSectionInputStyle('bank_transfer')}
                                                onFocus={(e) => handleInputFocus(e, 'bank_transfer')}
                                                onBlur={(e) => handleInputBlur(e, 'bank_transfer')}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={styles.sectionLabel}>Bank Name</label>
                                            <input
                                                type="text"
                                                name="bankName"
                                                value={paymentData.bankName}
                                                onChange={handleChange}
                                                placeholder="Enter bank name"
                                                style={getSectionInputStyle('bank_transfer')}
                                                onFocus={(e) => handleInputFocus(e, 'bank_transfer')}
                                                onBlur={(e) => handleInputBlur(e, 'bank_transfer')}
                                            />
                                        </div>
                                        <div>
                                            <label style={styles.sectionLabel}>Transaction Reference</label>
                                            <input
                                                type="text"
                                                name="transactionReference"
                                                value={paymentData.transactionReference}
                                                onChange={handleChange}
                                                placeholder="Enter transaction reference"
                                                style={getSectionInputStyle('bank_transfer')}
                                                onFocus={(e) => handleInputFocus(e, 'bank_transfer')}
                                                onBlur={(e) => handleInputBlur(e, 'bank_transfer')}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Online/Card Transaction ID */}
                                {(paymentData.method === 'online' || paymentData.method === 'card') && (
                                    <div style={styles.onlineSection}>
                                        <h4 style={styles.onlineTitle}>
                                            {paymentData.method === 'online' ? 'Online Payment' : 'Card Payment'} Details
                                        </h4>
                                        <div>
                                            <label style={styles.sectionLabel}>Transaction ID</label>
                                            <input
                                                type="text"
                                                name="transactionId"
                                                value={paymentData.transactionId}
                                                onChange={handleChange}
                                                placeholder="Enter transaction ID"
                                                style={getSectionInputStyle('online')}
                                                onFocus={(e) => handleInputFocus(e, 'online')}
                                                onBlur={(e) => handleInputBlur(e, 'online')}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Cash Payment Info */}
                                {paymentData.method === 'cash' && (
                                    <div style={styles.cashSection}>
                                        <p style={styles.cashText}>
                                            Cash Payment - No additional details required
                                        </p>
                                    </div>
                                )}

                                {/* Extra space at bottom for better scrolling */}
                                <div style={{ height: '20px' }}></div>
                            </div>

                            {/* Footer - Fixed */}
                            <div style={styles.footer}>
                                <button
                                    type="button"
                                    style={styles.cancelButton}
                                    onClick={onClose}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)';
                                        e.currentTarget.style.color = '#f1f5f9';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(100, 116, 139, 0.2)';
                                        e.currentTarget.style.color = '#94a3b8';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={styles.submitButton}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--accent)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--accent)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Save size={18} /> Save Payment Details</div>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PaymentModal;
// import React, { useState } from 'react';

// const PaymentModal = ({ isOpen, onClose, invoice, onSubmit }) => {
//     const [paymentData, setPaymentData] = useState({
//         method: 'cash',
//         transactionId: '',
//         upiId: '',
//         chequeNumber: '',
//         bankName: '',
//         chequeDate: '',
//         accountNumber: '',
//         ifscCode: '',
//         transactionReference: ''
//     });

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setPaymentData(prev => ({ ...prev, [name]: value }));
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();
//         onSubmit(paymentData);
//     };

//     // Glassmorphism Styles
//     const styles = {
//         overlay: {
//             position: 'fixed',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             background: 'rgba(0, 0, 0, 0.7)',
//             backdropFilter: 'blur(8px)',
//             WebkitBackdropFilter: 'blur(8px)',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             zIndex: 1050,
//             padding: '20px'
//         },
//         modalContainer: {
//             maxWidth: '650px',
//             width: '100%',
//             maxHeight: '90vh',
//             display: 'flex',
//             flexDirection: 'column',
//             background: 'var(--bg-card)',
//             backdropFilter: 'blur(30px)',
//             WebkitBackdropFilter: 'blur(30px)',
//             borderRadius: '20px',
//             boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 40px rgba(56, 189, 248, 0.2)',
//             border: '1px solid var(--border)',
//             position: 'relative',
//             overflow: 'hidden'
//         },
//         headerGlow: {
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             height: '1px',
//             background: 'var(--accent)',
//             zIndex: 1
//         },
//         modalHeader: {
//             background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.25), rgba(14, 165, 233, 0.15))',
//             color: 'var(--text-primary)',
//             padding: '20px 25px',
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             flexShrink: 0, // Prevent header from shrinking
//             borderBottom: '1px solid var(--border)',
//             position: 'relative'
//         },
//         modalTitle: {
//             margin: 0,
//             fontSize: '22px',
//             fontWeight: '600',
//             color: 'var(--text-primary)',
//             textShadow: '0 0 30px rgba(56, 189, 248, 0.5)'
//         },
//         closeButton: {
//             color: 'var(--text-primary)',
//             fontSize: '32px',
//             background: 'rgba(56, 189, 248, 0.2)',
//             borderRadius: '50%',
//             width: '40px',
//             height: '40px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             border: '1px solid var(--border)',
//             cursor: 'pointer',
//             transition: 'all 0.2s ease',
//             lineHeight: 1
//         },
//         scrollableBody: {
//             flex: 1, // Take remaining space
//             overflowY: 'auto',
//             overflowX: 'hidden',
//             padding: '25px',
//             // Custom scrollbar styling
//             scrollbarWidth: 'thin',
//             scrollbarColor: 'rgba(56, 189, 248, 0.3) rgba(15, 23, 42, 0.5)'
//         },
//         modalFooter: {
//             padding: '20px 25px',
//             background: 'var(--bg-card)',
//             borderTop: '1px solid var(--border)',
//             display: 'flex',
//             gap: '12px',
//             justifyContent: 'flex-end',
//             flexShrink: 0, // Prevent footer from shrinking
//             backdropFilter: 'blur(10px)',
//             WebkitBackdropFilter: 'blur(10px)'
//         },
//         invoiceSummary: {
//             marginBottom: '25px',
//             padding: '18px',
//             background: 'var(--bg-page)',
//             borderRadius: '12px',
//             borderLeft: '4px solid var(--accent)',
//             boxShadow: 'none'
//         },
//         summaryRow: {
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center'
//         },
//         summaryLabel: {
//             margin: '0 0 5px 0',
//             fontSize: '12px',
//             color: 'var(--text-secondary)',
//             fontWeight: '600',
//             textTransform: 'uppercase',
//             letterSpacing: '0.5px'
//         },
//         invoiceNumber: {
//             margin: 0,
//             fontSize: '18px',
//             color: 'var(--text-primary)',
//             fontWeight: '700',
//             textShadow: 'none'
//         },
//         amountLabel: {
//             margin: '0 0 5px 0',
//             fontSize: '12px',
//             color: 'var(--text-secondary)',
//             fontWeight: '600',
//             textTransform: 'uppercase',
//             letterSpacing: '0.5px',
//             textAlign: 'right'
//         },
//         totalAmount: {
//             margin: 0,
//             fontSize: '24px',
//             color: 'var(--accent)',
//             fontWeight: '700',
//             textShadow: 'none'
//         },
//         formGroup: {
//             marginBottom: '25px'
//         },
//         formLabel: {
//             fontSize: '14px',
//             fontWeight: '600',
//             marginBottom: '10px',
//             color: 'var(--text-secondary)',
//             display: 'block',
//             textTransform: 'uppercase',
//             letterSpacing: '0.5px'
//         },
//         formControl: {
//             width: '100%',
//             padding: '12px 15px',
//             fontSize: '15px',
//             borderRadius: '10px',
//             border: '1px solid var(--border)',
//             fontWeight: '500',
//             background: 'var(--bg-card)',
//             color: 'var(--text-primary)',
//             transition: 'all 0.2s ease',
//             outline: 'none',
//             boxSizing: 'border-box' // Ensure padding doesn't affect width
//         },
//         sectionBox: (bgColor, borderColor) => ({
//             padding: '20px',
//             background: bgColor,
//             borderRadius: '12px',
//             marginBottom: '20px',
//             border: `2px solid ${borderColor}`
//         }),
//         sectionTitle: (color) => ({
//             margin: '0 0 15px 0',
//             fontSize: '16px',
//             color: color,
//             fontWeight: '600',
//             textShadow: `0 0 15px ${color}40`
//         }),
//         cashInfo: {
//             padding: '18px',
//             background: 'var(--bg-page)',
//             borderRadius: '12px',
//             border: '2px dashed rgba(34, 197, 94, 0.4)',
//             textAlign: 'center',
//             marginBottom: '20px'
//         },
//         cashText: {
//             margin: 0,
//             fontSize: '15px',
//             color: 'var(--accent)',
//             fontWeight: '600',
//             textShadow: '0 0 10px rgba(34, 197, 94, 0.3)'
//         },
//         cancelButton: {
//             padding: '12px 28px',
//             fontSize: '15px',
//             fontWeight: '600',
//             borderRadius: '10px',
//             background: 'rgba(100, 116, 139, 0.3)',
//             color: 'var(--text-secondary)',
//             border: '1px solid rgba(100, 116, 139, 0.4)',
//             cursor: 'pointer',
//             transition: 'all 0.2s ease'
//         },
//         submitButton: {
//             padding: '12px 32px',
//             fontSize: '15px',
//             fontWeight: '600',
//             borderRadius: '10px',
//             background: 'var(--accent)',
//             color: '#fff',
//             border: 'none',
//             cursor: 'pointer',
//             transition: 'all 0.2s ease',
//             boxShadow: 'none',
//             display: 'flex',
//             alignItems: 'center',
//             gap: '8px'
//         }
//     };

//     // Custom scrollbar styles (can be added to CSS file)
//     const scrollbarStyles = `
//         .modal-scroll::-webkit-scrollbar {
//             width: 8px;
//         }
//         .modal-scroll::-webkit-scrollbar-track {
//             background: rgba(15, 23, 42, 0.5);
//             border-radius: 4px;
//         }
//         .modal-scroll::-webkit-scrollbar-thumb {
//             background: linear-gradient(180deg, rgba(56, 189, 248, 0.5), rgba(14, 165, 233, 0.3));
//             border-radius: 4px;
//             border: 2px solid rgba(15, 23, 42, 0.5);
//         }
//         .modal-scroll::-webkit-scrollbar-thumb:hover {
//             background: linear-gradient(180deg, rgba(56, 189, 248, 0.7), rgba(14, 165, 233, 0.5));
//         }
//     `;

//     if (!isOpen) return null;

//     return (
//         <>
//             {/* Inject scrollbar styles */}
//             <style>{scrollbarStyles}</style>

//             <div
//                 style={styles.overlay}
//                 onClick={onClose}
//             >
//                 <div
//                     style={styles.modalContainer}
//                     onClick={(e) => e.stopPropagation()}
//                 >
//                     {/* Header Glow */}
//                     <div style={styles.headerGlow}></div>

//                     {/* Modal Header - Sticky */}
//                     <div style={styles.modalHeader}>
//                         <h2 style={styles.modalTitle}>
//                             <CreditCard size={24} style={{ color: 'var(--accent)' }} /> Payment Details
//                         </h2>
//                         <button
//                             style={styles.closeButton}
//                             onClick={onClose}
//                             onMouseEnter={(e) => {
//                                 e.currentTarget.style.background = 'rgba(248, 113, 113, 0.3)';
//                                 e.currentTarget.style.borderColor = '#f87171';
//                                 e.currentTarget.style.color = '#f87171';
//                                 e.currentTarget.style.transform = 'rotate(90deg)';
//                             }}
//                             onMouseLeave={(e) => {
//                                 e.currentTarget.style.background = 'rgba(56, 189, 248, 0.2)';
//                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.3)';
//                                 e.currentTarget.style.color = '#38bdf8';
//                                 e.currentTarget.style.transform = 'rotate(0deg)';
//                             }}
//                         >
//                             ×
//                         </button>
//                     </div>

//                     {/* Scrollable Body */}
//                     <div
//                         className="modal-scroll"
//                         style={styles.scrollableBody}
//                     >
//                         <form onSubmit={handleSubmit}>
//                             {/* Invoice Summary */}
//                             <div style={styles.invoiceSummary}>
//                                 <div style={styles.summaryRow}>
//                                     <div>
//                                         <p style={styles.summaryLabel}>
//                                             INVOICE NUMBER
//                                         </p>
//                                         <p style={styles.invoiceNumber}>
//                                             {invoice?.invoiceNumber}
//                                         </p>
//                                     </div>
//                                     <div>
//                                         <p style={styles.amountLabel}>
//                                             TOTAL AMOUNT
//                                         </p>
//                                         <p style={styles.totalAmount}>
//                                             ₹{invoice?.grandTotal?.toFixed(2)}
//                                         </p>
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Payment Method Selection */}
//                             <div style={styles.formGroup}>
//                                 <label style={styles.formLabel}>
//                                     <Wallet size={16} style={{ color: 'var(--accent)' }} /> Payment Method *
//                                 </label>
//                                 <select
//                                     name="method"
//                                     value={paymentData.method}
//                                     onChange={handleChange}
//                                     required
//                                     style={styles.formControl}
//                                     onFocus={(e) => {
//                                         e.currentTarget.style.borderColor = '#38bdf8';
//                                         e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2), 0 0 20px rgba(56, 189, 248, 0.2)';
//                                     }}
//                                     onBlur={(e) => {
//                                         e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                         e.currentTarget.style.boxShadow = 'none';
//                                     }}
//                                 >
//                                     <option value="cash">Cash</option>
//                                     <option value="online">Online Payment</option>
//                                     <option value="card">Card</option>
//                                     <option value="upi">UPI</option>
//                                     <option value="bank_transfer">Bank Transfer</option>
//                                     <option value="cheque">Cheque</option>
//                                 </select>
//                             </div>

//                             {/* UPI Fields */}
//                             {paymentData.method === 'upi' && (
//                                 <div style={styles.sectionBox(
//                                     'rgba(251, 191, 36, 0.1)',
//                                     'rgba(251, 191, 36, 0.4)'
//                                 )}>
//                                     <h4 style={styles.sectionTitle('#fbbf24')}>
//                                         UPI Payment Details
//                                     </h4>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             UPI ID
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="upiId"
//                                             value={paymentData.upiId}
//                                             onChange={handleChange}
//                                             placeholder="example@upi"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#fbbf24';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(251, 191, 36, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Transaction ID
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="transactionId"
//                                             value={paymentData.transactionId}
//                                             onChange={handleChange}
//                                             placeholder="Enter UPI transaction ID"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#fbbf24';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(251, 191, 36, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                 </div>
//                             )}

//                             {/* Cheque Fields */}
//                             {paymentData.method === 'cheque' && (
//                                 <div style={styles.sectionBox(
//                                     'rgba(168, 85, 247, 0.1)',
//                                     'rgba(168, 85, 247, 0.4)'
//                                 )}>
//                                     <h4 style={styles.sectionTitle('#a855f7')}>
//                                         Cheque Payment Details
//                                     </h4>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Cheque Number
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="chequeNumber"
//                                             value={paymentData.chequeNumber}
//                                             onChange={handleChange}
//                                             placeholder="Enter cheque number"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#a855f7';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168, 85, 247, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Bank Name
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="bankName"
//                                             value={paymentData.bankName}
//                                             onChange={handleChange}
//                                             placeholder="Enter bank name"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#a855f7';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168, 85, 247, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Cheque Date
//                                         </label>
//                                         <input
//                                             type="date"
//                                             name="chequeDate"
//                                             value={paymentData.chequeDate}
//                                             onChange={handleChange}
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#a855f7';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168, 85, 247, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                 </div>
//                             )}

//                             {/* Bank Transfer Fields */}
//                             {paymentData.method === 'bank_transfer' && (
//                                 <div style={styles.sectionBox(
//                                     'rgba(56, 189, 248, 0.1)',
//                                     'rgba(56, 189, 248, 0.4)'
//                                 )}>
//                                     <h4 style={styles.sectionTitle('#38bdf8')}>
//                                         Bank Transfer Details
//                                     </h4>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Account Number
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="accountNumber"
//                                             value={paymentData.accountNumber}
//                                             onChange={handleChange}
//                                             placeholder="Enter account number"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#38bdf8';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             IFSC Code
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="ifscCode"
//                                             value={paymentData.ifscCode}
//                                             onChange={handleChange}
//                                             placeholder="Enter IFSC code"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#38bdf8';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div style={{ marginBottom: '15px' }}>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Bank Name
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="bankName"
//                                             value={paymentData.bankName}
//                                             onChange={handleChange}
//                                             placeholder="Enter bank name"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#38bdf8';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                     <div>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Transaction Reference
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="transactionReference"
//                                             value={paymentData.transactionReference}
//                                             onChange={handleChange}
//                                             placeholder="Enter transaction reference"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#38bdf8';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 189, 248, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                 </div>
//                             )}

//                             {/* Online/Card Transaction ID */}
//                             {(paymentData.method === 'online' || paymentData.method === 'card') && (
//                                 <div style={styles.sectionBox(
//                                     'rgba(34, 197, 94, 0.1)',
//                                     'rgba(34, 197, 94, 0.4)'
//                                 )}>
//                                     <h4 style={styles.sectionTitle('#22c55e')}>
//                                         {paymentData.method === 'online' ? 'Online Payment' : 'Card Payment'} Details
//                                     </h4>
//                                     <div>
//                                         <label style={{ ...styles.formLabel, fontSize: '13px' }}>
//                                             Transaction ID
//                                         </label>
//                                         <input
//                                             type="text"
//                                             name="transactionId"
//                                             value={paymentData.transactionId}
//                                             onChange={handleChange}
//                                             placeholder="Enter transaction ID"
//                                             style={styles.formControl}
//                                             onFocus={(e) => {
//                                                 e.currentTarget.style.borderColor = '#22c55e';
//                                                 e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.2)';
//                                             }}
//                                             onBlur={(e) => {
//                                                 e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
//                                                 e.currentTarget.style.boxShadow = 'none';
//                                             }}
//                                         />
//                                     </div>
//                                 </div>
//                             )}

//                             {/* Cash Payment Info */}
//                             {paymentData.method === 'cash' && (
//                                 <div style={styles.cashInfo}>
//                                     <p style={styles.cashText}>
//                                         Cash Payment - No additional details required
//                                     </p>
//                                 </div>
//                             )}

//                             {/* Modal Footer - Inside scrollable area */}
//                             <div style={styles.modalFooter}>
//                                 <button
//                                     type="button"
//                                     onClick={onClose}
//                                     style={styles.cancelButton}
//                                     onMouseEnter={(e) => {
//                                         e.currentTarget.style.background = 'rgba(100, 116, 139, 0.5)';
//                                         e.currentTarget.style.color = '#f1f5f9';
//                                         e.currentTarget.style.transform = 'translateY(-2px)';
//                                     }}
//                                     onMouseLeave={(e) => {
//                                         e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)';
//                                         e.currentTarget.style.color = '#94a3b8';
//                                         e.currentTarget.style.transform = 'translateY(0)';
//                                     }}
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     style={styles.submitButton}
//                                     onMouseEnter={(e) => {
//                                         e.currentTarget.style.backgroundColor = 'var(--accent)';
//                                         e.currentTarget.style.transform = 'translateY(-2px)';
//                                         e.currentTarget.style.boxShadow = 'none';
//                                     }}
//                                     onMouseLeave={(e) => {
//                                         e.currentTarget.style.backgroundColor = 'var(--accent)';
//                                         e.currentTarget.style.transform = 'translateY(0)';
//                                         e.currentTarget.style.boxShadow = 'none';
//                                     }}
//                                 >
//                                     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Save size={18} /> Save Payment Details</div>
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             </div>
//         </>
//     );
// };

// export default PaymentModal;
